import { Head, Link, usePage } from "@inertiajs/react"
import { Pencil, Plus } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { AdminPageHeader, AdminSection, EmptyState } from "@/components/admin/AdminPageHeader"
import { ConfirmDestroy } from "@/components/admin/ConfirmDestroy"
import { plural } from "@/components/admin/format"
import { MoveButtons } from "@/components/admin/MoveButtons"
import { ResponsiveList } from "@/components/admin/ResponsiveList"
import { Button } from "@/components/ui/button"
import type { AdminFaqRow, AdminFaqsIndexProps, PageProps } from "@/types/inertia"

const TITLE = "FAQs"
const DESCRIPTION =
  "The questions on the public FAQs page, by topic: add, edit, reorder within a topic, or remove a question."

export default function AdminFaqsIndex() {
  const { props } = usePage<PageProps<AdminFaqsIndexProps>>()
  const { groups } = props
  const count = groups.reduce((sum, group) => sum + group.faqs.length, 0)

  return (
    <>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <AdminPageHeader
          title="FAQs"
          description={`${plural(count, "question")} on the FAQs page, by topic, in this order. Empty topics are hidden on the site.`}
          actions={
            <Button asChild size="lg">
              <Link href="/admin/faqs/new">
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add a question
              </Link>
            </Button>
          }
        />

        {groups.map((group) => {
          const headingId = `faq-topic-${group.topic}`
          return (
            <AdminSection
              key={group.topic}
              id={headingId}
              title={group.label}
              action={
                <Button asChild variant="ghost" size="lg">
                  <Link href={`/admin/faqs/new?topic=${encodeURIComponent(group.topic)}`}>
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Add<span className="sr-only"> a question to {group.label}</span>
                  </Link>
                </Button>
              }
            >
              {group.faqs.length === 0 ? (
                <EmptyState>No questions in this topic yet.</EmptyState>
              ) : (
                <ResponsiveList
                  label={`${group.label} questions`}
                  items={group.faqs}
                  getKey={(faq) => faq.id}
                  card={(faq) => <FaqCard faq={faq} />}
                  columns={[
                    {
                      key: "order",
                      header: "Order",
                      className: "w-28",
                      cell: (faq) => (
                        <MoveButtons url={faq.move_url} itemName={faq.question} first={faq.first} last={faq.last} />
                      ),
                    },
                    {
                      key: "question",
                      header: "Question",
                      cell: (faq) => <FaqText faq={faq} />,
                    },
                    {
                      key: "actions",
                      header: "Actions",
                      srOnlyHeader: true,
                      className: "w-60 whitespace-nowrap text-right",
                      cell: (faq) => <FaqActions faq={faq} />,
                    },
                  ]}
                />
              )}
            </AdminSection>
          )
        })}
      </AdminShell>
    </>
  )
}

function FaqText({ faq, linked = true }: { faq: AdminFaqRow; linked?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="font-medium">
        {linked ? <Link href={faq.edit_url}>{faq.question}</Link> : <span className="text-ink-display">{faq.question}</span>}
      </div>
      <div className="line-clamp-2 text-xs text-ink-muted">{faq.answer}</div>
    </div>
  )
}

function FaqActions({ faq }: { faq: AdminFaqRow }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-1 sm:flex-nowrap">
      <Button asChild variant="secondary" size="lg">
        <Link href={faq.edit_url}>
          <Pencil className="h-4 w-4" aria-hidden="true" />
          Edit<span className="sr-only"> “{faq.question}”</span>
        </Link>
      </Button>
      <ConfirmDestroy
        url={faq.destroy_url}
        itemName={`“${faq.question}”`}
        title="Remove this question?"
        description={`“${faq.question}” leaves the FAQs page straight away. This cannot be undone.`}
      />
    </div>
  )
}

function FaqCard({ faq }: { faq: AdminFaqRow }) {
  return (
    <div className="px-4 py-3">
      {/* Text, not a link: the 44px Edit button below is the card's way in. */}
      <div className="text-sm">
        <FaqText faq={faq} linked={false} />
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <MoveButtons url={faq.move_url} itemName={faq.question} first={faq.first} last={faq.last} />
        <FaqActions faq={faq} />
      </div>
    </div>
  )
}
